// Rules for VALIDATION_CONSTRAINTS.md section 7 — intermediate event content.
// Cardinality (7.1) is covered by rules 3.4 and 3.5 and intentionally skipped here.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import { labelOf } from '../helpers';

// 7.1: Skipped — intermediate-event cardinality (1 in / 1 out) is enforced by rules 3.4 and 3.5.

// 7.2: IntermediateCatchMessageEvent requires a non-empty messageName.
const rule_7_2_IntermediateCatchMessage: ValidationRule = {
  id: '7.2',
  description: 'IntermediateCatchMessageEvent requires a non-empty messageName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'IntermediateCatchMessageEvent') continue;
      const name = (n.data?.messageName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'error',
          rule: '7.2',
          message: `${labelOf(n)} requires a non-empty messageName.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_7_2_IntermediateCatchMessage);

// 7.3: IntermediateCatchTimerEvent requires a timerDefinition or timerDate.
const rule_7_3_IntermediateCatchTimer: ValidationRule = {
  id: '7.3',
  description: 'IntermediateCatchTimerEvent requires a timerDefinition or timerDate',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'IntermediateCatchTimerEvent') continue;
      const def = (n.data?.timerDefinition ?? '').toString().trim();
      const date = (n.data?.timerDate ?? '').toString().trim();
      if (!def && !date) {
        issues.push({
          severity: 'error',
          rule: '7.3',
          message: `${labelOf(n)} requires a timerDefinition or timerDate.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_7_3_IntermediateCatchTimer);

// 7.4: IntermediateCatchSignalEvent requires a non-empty signalName.
const rule_7_4_IntermediateCatchSignal: ValidationRule = {
  id: '7.4',
  description: 'IntermediateCatchSignalEvent requires a non-empty signalName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'IntermediateCatchSignalEvent') continue;
      const name = (n.data?.signalName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'error',
          rule: '7.4',
          message: `${labelOf(n)} requires a non-empty signalName.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_7_4_IntermediateCatchSignal);

// 7.5: IntermediateThrowMessageEvent requires a non-empty messageName.
//      Engine accesses Message.VariableName/GetName/Payload unconditionally.
const rule_7_5_IntermediateThrowMessage: ValidationRule = {
  id: '7.5',
  description: 'IntermediateThrowMessageEvent requires a non-empty messageName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'IntermediateThrowMessageEvent') continue;
      const name = (n.data?.messageName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'error',
          rule: '7.5',
          message: `${labelOf(n)} requires a non-empty messageName.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_7_5_IntermediateThrowMessage);

// 7.6: IntermediateThrowSignalEvent requires a non-empty signalName.
const rule_7_6_IntermediateThrowSignal: ValidationRule = {
  id: '7.6',
  description: 'IntermediateThrowSignalEvent requires a non-empty signalName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'IntermediateThrowSignalEvent') continue;
      const name = (n.data?.signalName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'error',
          rule: '7.6',
          message: `${labelOf(n)} requires a non-empty signalName.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_7_6_IntermediateThrowSignal);

// 7.7: IntermediateThrowErrorEvent requires exceptionType or errorMessage.
const rule_7_7_IntermediateThrowError: ValidationRule = {
  id: '7.7',
  description: 'IntermediateThrowErrorEvent requires exceptionType or errorMessage',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'IntermediateThrowErrorEvent') continue;
      const exceptionType = ((n.data as any)?.exceptionType ?? '').toString().trim();
      const errorMessage = (n.data?.errorMessage ?? '').toString().trim();
      if (!exceptionType && !errorMessage) {
        issues.push({
          severity: 'error',
          rule: '7.7',
          message: `${labelOf(n)} requires an exceptionType or errorMessage.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_7_7_IntermediateThrowError);

// 7.7b: IntermediateThrowEscalationEvent should have an escalationName.
//       Field not yet declared in WorkflowNodeData — warn rather than block save.
const rule_7_7b_IntermediateThrowEscalation: ValidationRule = {
  id: '7.7b',
  description: 'IntermediateThrowEscalationEvent should have an escalationName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'IntermediateThrowEscalationEvent') continue;
      const name = ((n.data as any)?.escalationName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'warning',
          rule: '7.7b',
          message: `${labelOf(n)} has no escalationName — escalation handling is not wired up.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_7_7b_IntermediateThrowEscalation);
